import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import logo from "../assets/tiply_logo_v5.png";

/* ═══════════════════════════════════════════════
   HOOKS & HELPERS
   ═══════════════════════════════════════════════ */

function useInView() {
    const ref = useRef(null);
    const [isInView, setIsInView] = useState(false);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) { setIsInView(true); observer.unobserve(el); } },
            { threshold: 0.15 }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);
    return [ref, isInView];
}

function AnimatedSection({ children, className = "", delay = 0 }) {
    const [ref, inView] = useInView();
    return (
        <div
            ref={ref}
            className={`${className} transition-all duration-700 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
            style={{ transitionDelay: `${delay}ms` }}
        >
            {children}
        </div>
    );
}

function FAQItem({ question, answer }) {
    const [open, setOpen] = useState(false);
    const contentRef = useRef(null);
    const [height, setHeight] = useState(0);

    useEffect(() => {
        if (contentRef.current) setHeight(contentRef.current.scrollHeight);
    }, [answer]);

    return (
        <div className="border-b border-gray-200">
            <button
                onClick={() => setOpen(!open)}
                className="w-full flex items-center justify-between py-5 text-left group"
            >
                <span className="text-base font-semibold text-gray-900 group-hover:text-gray-700 transition pr-4">{question}</span>
                <span
                    className={`flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full border-2 border-gray-300 transition-all duration-300 ${open ? "rotate-45 border-greenmint" : ""}`}
                >
                    <svg className="w-3.5 h-3.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                    </svg>
                </span>
            </button>
            <div
                className="overflow-hidden transition-all duration-400 ease-in-out"
                style={{ maxHeight: open ? height : 0 }}
            >
                <div ref={contentRef} className="pb-5 text-sm text-gray-600 leading-relaxed pr-8">
                    {answer}
                </div>
            </div>
        </div>
    );
}

/* ═══════════════════════════════════════════════
   REWARD TIERS DATA
   ═══════════════════════════════════════════════ */

const TIERS = [
    { tips: 2000, reward: 100 },
    { tips: 4000, reward: 200 },
    { tips: 6000, reward: 300 },
    { tips: 8000, reward: 400 },
    { tips: 10000, reward: 500 },
];

const MAILTO_LINK = "mailto:team@tiply.ee?subject=Tiply%20Referral%20Program%20—%20I%20want%20to%20refer%20a%20business&body=Hi%20Tiply%20team%2C%0A%0AI'd%20like%20to%20refer%20a%20business%20to%20Tiply.%0A%0ABusiness%20name%3A%20%0AMy%20name%3A%20%0AMy%20email%3A%20%0A%0AThanks!";

/* ═══════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════ */

export default function ReferralPage() {
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <div className="font-urbanist text-gray-900 bg-white min-h-screen">
            {/* ─── NAVBAR ─── */}
            <nav
                className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                    scrolled ? "bg-white/95 backdrop-blur shadow-sm" : "bg-transparent"
                }`}
            >
                <div className="max-w-6xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
                    <div className="flex items-center gap-4">
                        <Link to="/">
                            <img src={logo} alt="Tiply" className="h-9 w-auto" />
                        </Link>
                        <Link to="/" className="text-sm text-gray-500 hover:text-gray-900 transition hidden sm:inline">
                            &larr; Back to Home
                        </Link>
                    </div>
                    <a
                        href={MAILTO_LINK}
                        className="bg-black text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-neutral-800 transition"
                    >
                        Start Referring
                    </a>
                </div>
            </nav>

            {/* ─── HERO ─── */}
            <section className="pt-28 pb-20 md:pt-36 md:pb-28 relative overflow-hidden">
                {/* Background decoration */}
                <div className="absolute inset-0 -z-10">
                    <div className="absolute top-20 -right-20 w-72 h-72 bg-greenmint/20 rounded-full blur-3xl" />
                    <div className="absolute bottom-10 -left-20 w-60 h-60 bg-mint/30 rounded-full blur-3xl" />
                </div>

                <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
                    <AnimatedSection>
                        <div className="inline-block bg-greenmint/15 text-sm font-semibold text-gray-700 px-4 py-1.5 rounded-full mb-6">
                            Tiply Referral Program
                        </div>
                    </AnimatedSection>
                    <AnimatedSection delay={100}>
                        <h1 className="text-4xl md:text-6xl font-bold leading-tight mb-6">
                            Earn up to{" "}
                            <span className="bg-gradient-to-r from-emerald-500 to-greenmint bg-clip-text text-transparent">€500</span>
                            {" "}by sharing Tiply
                        </h1>
                    </AnimatedSection>
                    <AnimatedSection delay={200}>
                        <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto mb-8">
                            Tell your manager about Tiply. When they sign up and start receiving digital tips, you earn cash rewards — it's that simple.
                        </p>
                    </AnimatedSection>
                    <AnimatedSection delay={300}>
                        <a
                            href={MAILTO_LINK}
                            className="inline-block bg-black text-white px-8 py-3.5 rounded-lg text-base font-semibold hover:bg-neutral-800 hover:scale-[1.02] transition-all duration-200"
                        >
                            Start Referring
                        </a>
                        <p className="mt-4 text-sm text-gray-500">
                            No sign-up needed — just send us an email
                        </p>
                    </AnimatedSection>
                </div>
            </section>

            {/* ─── HOW IT WORKS ─── */}
            <section className="py-20 md:py-28 bg-gray-50">
                <div className="max-w-5xl mx-auto px-4 md:px-6">
                    <AnimatedSection className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold">How it works</h2>
                        <p className="mt-3 text-gray-500 max-w-xl mx-auto">Three simple steps to start earning</p>
                    </AnimatedSection>

                    <div className="grid md:grid-cols-3 gap-8 relative">
                        {/* Connector line */}
                        <div className="hidden md:block absolute top-8 left-[calc(16.67%+20px)] right-[calc(16.67%+20px)] h-0.5 bg-gray-200" />

                        {[
                            {
                                step: "1",
                                title: "Share Tiply",
                                desc: "Tell your manager about Tiply, or simply share our website link with them. It takes 30 seconds.",
                                icon: (
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                                    </svg>
                                ),
                            },
                            {
                                step: "2",
                                title: "They sign up",
                                desc: "Your manager registers their business on Tiply and starts accepting digital tips from customers.",
                                icon: (
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                    </svg>
                                ),
                            },
                            {
                                step: "3",
                                title: "You earn",
                                desc: "As tips flow through Tiply, you unlock cash rewards at each milestone — up to €500 total.",
                                icon: (
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                ),
                            },
                        ].map((item, i) => (
                            <AnimatedSection key={i} delay={i * 150}>
                                <div className="text-center relative">
                                    <div className="w-16 h-16 bg-white border-2 border-greenmint rounded-full flex items-center justify-center mx-auto mb-4 relative z-10 text-greenmint">
                                        {item.icon}
                                    </div>
                                    <div className="text-xs font-bold text-greenmint mb-2">STEP {item.step}</div>
                                    <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                                    <p className="text-sm text-gray-600">{item.desc}</p>
                                </div>
                            </AnimatedSection>
                        ))}
                    </div>
                </div>
            </section>

            {/* ─── REWARD TIERS ─── */}
            <section className="py-20 md:py-28 bg-white">
                <div className="max-w-4xl mx-auto px-4 md:px-6">
                    <AnimatedSection className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold">Your rewards, step by step</h2>
                        <p className="mt-3 text-gray-500 max-w-xl mx-auto">
                            The more tips the business processes through Tiply, the more you earn
                        </p>
                    </AnimatedSection>

                    {/* Tier visualization */}
                    <AnimatedSection>
                        <div className="bg-gray-50 rounded-2xl p-6 md:p-10">
                            {/* Progress track */}
                            <div className="relative">
                                {/* Vertical line */}
                                <div className="absolute left-6 md:left-8 top-0 bottom-0 w-0.5 bg-gray-200" />

                                <div className="space-y-0">
                                    {TIERS.map((tier, i) => {
                                        const isLast = i === TIERS.length - 1;
                                        return (
                                            <div key={i} className="relative flex items-start gap-4 md:gap-6 pb-8 last:pb-0">
                                                {/* Node */}
                                                <div
                                                    className={`relative z-10 flex-shrink-0 w-12 h-12 md:w-16 md:h-16 rounded-full flex items-center justify-center font-bold text-sm md:text-base ${
                                                        isLast
                                                            ? "bg-gradient-to-br from-emerald-400 to-greenmint text-white shadow-lg shadow-greenmint/30"
                                                            : "bg-white border-2 border-gray-200 text-gray-700"
                                                    }`}
                                                >
                                                    €{tier.reward}
                                                </div>

                                                {/* Content */}
                                                <div className={`flex-1 pt-1 md:pt-3 ${isLast ? "" : ""}`}>
                                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                                                        <div>
                                                            <span className="font-bold text-gray-900">
                                                                €{tier.tips.toLocaleString()} in tips processed
                                                            </span>
                                                        </div>
                                                        <div
                                                            className={`text-sm font-semibold ${
                                                                isLast ? "text-emerald-600" : "text-gray-500"
                                                            }`}
                                                        >
                                                            You earn €{tier.reward}
                                                            {isLast && " — maximum reward!"}
                                                        </div>
                                                    </div>
                                                    {/* Progress bar for this tier */}
                                                    <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                                                        <div
                                                            className="h-full bg-gradient-to-r from-emerald-400 to-greenmint rounded-full transition-all duration-1000"
                                                            style={{ width: `${(tier.tips / 10000) * 100}%` }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Big callout */}
                            <div className="mt-8 pt-8 border-t border-gray-200 text-center">
                                <div className="text-4xl md:text-5xl font-extrabold font-bebas tracking-wide">
                                    <span className="bg-gradient-to-r from-emerald-500 to-greenmint bg-clip-text text-transparent">€500</span>
                                    <span className="text-gray-900"> in your pocket</span>
                                </div>
                                <p className="text-gray-500 mt-2">Just for sharing a link with your manager</p>
                            </div>
                        </div>
                    </AnimatedSection>

                    {/* Legal note */}
                    <p className="text-xs text-gray-400 text-center mt-4">
                        * Tips must be processed within 6 months of the business signing up.
                        See full terms below.
                    </p>
                </div>
            </section>

            {/* ─── WHY REFER ─── */}
            <section className="py-20 md:py-28 bg-gray-50">
                <div className="max-w-5xl mx-auto px-4 md:px-6">
                    <AnimatedSection className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold">Why refer?</h2>
                        <p className="mt-3 text-gray-500 max-w-xl mx-auto">
                            It's not just about the bonus — everyone wins
                        </p>
                    </AnimatedSection>

                    <div className="grid md:grid-cols-3 gap-6">
                        {[
                            {
                                title: "A simpler digital tipping experience",
                                desc: "When your workplace uses Tiply, tips can be routed to your bank account after Tiply fees. Local tax and reporting obligations may apply.",
                                icon: (
                                    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                    </svg>
                                ),
                            },
                            {
                                title: "Cash rewards on top",
                                desc: "Earn up to €500 in referral bonuses as your workplace processes tips through Tiply. Paid directly to your bank account.",
                                icon: (
                                    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                ),
                            },
                            {
                                title: "Help your whole team",
                                desc: "Every colleague benefits when digital tipping is set up. More customers will tip when it's easy and cashless.",
                                icon: (
                                    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                    </svg>
                                ),
                            },
                        ].map((item, i) => (
                            <AnimatedSection key={i} delay={i * 150}>
                                <div className="bg-white p-6 rounded-2xl border border-gray-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                                    <div className="w-12 h-12 bg-greenmint/15 rounded-xl flex items-center justify-center text-greenmint mb-4">
                                        {item.icon}
                                    </div>
                                    <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                                    <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
                                </div>
                            </AnimatedSection>
                        ))}
                    </div>
                </div>
            </section>

            {/* ─── FAQ ─── */}
            <section className="py-20 md:py-28 bg-white">
                <div className="max-w-3xl mx-auto px-4 md:px-6">
                    <AnimatedSection className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold">Questions & answers</h2>
                    </AnimatedSection>

                    <AnimatedSection>
                        <div>
                            <FAQItem
                                question="How do I refer a business?"
                                answer="Simply email us at team@tiply.ee with the name of the business and your contact details. We'll take it from there and keep you updated on the progress."
                            />
                            <FAQItem
                                question="When do I get paid?"
                                answer="Rewards are paid out within 30 days of reaching each tier milestone. For example, once the business you referred has processed €2,000 in tips, you'll receive your first €100 within 30 days."
                            />
                            <FAQItem
                                question='What counts as "tips processed"?'
                                answer="All tips received by the business through Tiply within 6 months of their signup count toward the reward tiers. This includes tips from all employees at the business, not just your own."
                            />
                            <FAQItem
                                question="Can I refer multiple businesses?"
                                answer="Yes! Each referral earns its own rewards independently. If you refer 3 businesses and each reaches the €10,000 milestone, you could earn up to €1,500 total."
                            />
                            <FAQItem
                                question="Do I need to work at the business?"
                                answer="No — anyone can refer a business. However, you should have a genuine connection to the business. We verify all referrals to ensure the program is used fairly."
                            />
                            <FAQItem
                                question="What if the business stops using Tiply?"
                                answer="You keep all rewards already earned. Future tiers are based on cumulative tips processed, so if the business resumes using Tiply later, the counter picks up where it left off."
                            />
                        </div>
                    </AnimatedSection>
                </div>
            </section>

            {/* ─── CTA ─── */}
            <section className="py-20 md:py-28 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" />
                <div className="absolute top-10 right-10 w-64 h-64 bg-greenmint/10 rounded-full blur-3xl" />
                <div className="absolute bottom-10 left-10 w-48 h-48 bg-emerald-500/10 rounded-full blur-3xl" />

                <div className="relative max-w-3xl mx-auto px-4 md:px-6 text-center">
                    <AnimatedSection>
                        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                            Ready to start earning?
                        </h2>
                    </AnimatedSection>
                    <AnimatedSection delay={150}>
                        <p className="text-lg text-gray-400 mb-8 max-w-xl mx-auto">
                            Send us a quick email with the business name and your details. We'll handle the rest.
                        </p>
                    </AnimatedSection>
                    <AnimatedSection delay={300}>
                        <a
                            href={MAILTO_LINK}
                            className="inline-block bg-greenmint text-gray-900 px-8 py-3.5 rounded-lg text-base font-bold hover:bg-emerald-300 hover:scale-[1.02] transition-all duration-200"
                        >
                            Refer a Business
                        </a>
                        <p className="mt-4 text-sm text-gray-500">
                            Or email us directly at{" "}
                            <a href="mailto:team@tiply.ee" className="font-medium text-gray-400 underline underline-offset-2 hover:text-white transition">
                                team@tiply.ee
                            </a>
                        </p>
                    </AnimatedSection>
                </div>
            </section>

            {/* ─── LEGAL DISCLAIMER ─── */}
            <section className="py-10 bg-gray-50 border-t border-gray-200">
                <div className="max-w-3xl mx-auto px-4 md:px-6">
                    <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Referral Program Terms</h3>
                    <div className="text-xs text-gray-400 space-y-1.5 leading-relaxed">
                        <p>
                            Referral rewards are paid after each milestone is reached. Tips must be processed within 6 months of the referred business's signup date to count toward reward tiers.
                        </p>
                        <p>
                            Maximum reward per referral is €500 (cumulative across all tiers). Rewards are paid via bank transfer within 30 days of milestone completion.
                        </p>
                        <p>
                            Tiply OÜ reserves the right to modify, suspend, or discontinue the referral program at any time. Self-referrals and fraudulent referrals will be disqualified. Tiply reserves the right to withhold rewards if referral activity is deemed fraudulent or in violation of these terms.
                        </p>
                        <p>
                            By participating in the referral program, you agree to these terms. For questions, contact{" "}
                            <a href="mailto:team@tiply.ee" className="underline hover:text-gray-600 transition">team@tiply.ee</a>.
                        </p>
                    </div>
                </div>
            </section>

            {/* ─── FOOTER ─── */}
            <footer className="bg-gray-900 text-gray-400 py-12">
                <div className="max-w-6xl mx-auto px-4 md:px-6">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                        <img src={logo} alt="Tiply" className="h-8 w-auto" />
                        <div className="flex items-center gap-6 text-sm">
                            <Link to="/" className="hover:text-white transition">Home</Link>
                            <a href="/privacy" className="hover:text-white transition">Privacy</a>
                            <a href="/terms" className="hover:text-white transition">Terms</a>
                            <a href="mailto:team@tiply.ee" className="hover:text-white transition">Contact</a>
                        </div>
                    </div>
                    <div className="mt-8 pt-6 border-t border-gray-800 text-center text-xs text-gray-500">
                        &copy; {new Date().getFullYear()} Tiply. All rights reserved.
                    </div>
                </div>
            </footer>
        </div>
    );
}
